import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectMasterCompany } from 'store/auth/selectors';
import {
  selectExtendedBorrowers,
  selectExtendedSubjectProperty,
  selectFormattedLoanFeesView,
  selectLoanProgramRate,
  selectTotalLoanAmount,
  selectUnderwritingExpirationDates,
} from 'store/loanApplications/selectors';
import { selectApplicationConsentRecords } from 'store/loanApplications/selectors/loanConsents.selectors';

import { computeReadinessChecks, summarizeReadiness } from '../../readiness/readinessChecks.utils';

import {
  INITIAL_LE_PACKAGE_NAME,
  resolveFees,
  resolveHasAppraisedValue,
  toPositiveNumber,
} from './utils';

const useDisclosureReadiness = () => {
  const expirationDates = useSelector(selectUnderwritingExpirationDates);
  const extendedBorrowers = useSelector(selectExtendedBorrowers);
  const consentRecords = useSelector(selectApplicationConsentRecords);
  const feesView = useSelector(selectFormattedLoanFeesView);
  const subjectProperty = useSelector(selectExtendedSubjectProperty);
  const totalLoanAmount = useSelector(selectTotalLoanAmount);
  const loanProgramRate = useSelector(selectLoanProgramRate);
  const masterCompany = useSelector(selectMasterCompany);

  const borrowers = useMemo(
    () =>
      (extendedBorrowers ?? []).map(({ borrowerId, fullName, email }) => ({
        id: borrowerId,
        fullName,
        email,
      })),
    [extendedBorrowers],
  );

  const checks = useMemo(
    () =>
      computeReadinessChecks({
        ecd: { earliestClosingDate: expirationDates?.earliestClosingDate },
        consentRecords,
        borrowers,
        compensation: { isResolved: false },
        fees: resolveFees(feesView),
        loanFields: { hasAppraisedValue: resolveHasAppraisedValue(subjectProperty) },
      }),
    [expirationDates, consentRecords, borrowers, feesView, subjectProperty],
  );

  const summary = useMemo(() => summarizeReadiness(checks), [checks]);

  const snapshot = useMemo(
    () => ({
      packageName: INITIAL_LE_PACKAGE_NAME,
      loanAmount: toPositiveNumber(totalLoanAmount),
      rate: loanProgramRate,
      signerCount: borrowers.length,
      creditor: masterCompany?.name ?? null,
    }),
    [totalLoanAmount, loanProgramRate, borrowers.length, masterCompany],
  );

  return { checks, summary, snapshot };
};

export default useDisclosureReadiness;
